import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retryWhen, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AppInsightsService, SeverityLevel } from '@markpieszak/ng-application-insights';
import { SnackbarService } from '../snackbar/snackbar.service';
import { RetryHeaderName, WillHandleErrorMessagingHeaderName } from '../utility/http-options';
import { genericRetryStrategy } from './generic-retry-strategy';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private appInsightsService: AppInsightsService,
    private snackbarService: SnackbarService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retryWhen(
        genericRetryStrategy({
          maxRetryAttempts:
            request.method === 'GET' || request.headers.get(RetryHeaderName) === 'true' ? 1 : 0,
          scalingDuration: 2000,
          excludedStatusCodes: [400, 401, 403, 429, 500, 502, 503, 504],
          //Exclude common statuses that will likely fail again or apply additional load under stress
        }),
      ),
      catchError((error: HttpErrorResponse) => {
        let invalidMessages: string = '';

        if (error.error && error.error.errors) {
          for (const [key, value] of Object.entries(error.error.errors)) {
            const arrayValue = Array.isArray(value) ? (value as Array<any>) : null;
            invalidMessages += key.toString();
            invalidMessages += ': ';
            invalidMessages += arrayValue !== null ? arrayValue.join(', ') : value.toString();
            invalidMessages += '; ';
          }
        }

        this.appInsightsService.trackException(
          error,
          null,
          {
            name: error.name,
            ok: String(error.ok),
            status: String(error.status),
            url: error.url,
            message: invalidMessages,
          },
          null,
          [400, 401, 403, 404].includes(error.status)
            ? SeverityLevel.Information
            : SeverityLevel.Error,
        );

        // Display generic error message if request fails due to anything but not found or auth.
        // Subsequent snackbar failure messages will override the display of this one if the error messaging
        // is not bypassed by including the header WillHandleErrorMessaging set to true.
        if (
          request.headers.get(WillHandleErrorMessagingHeaderName) !== 'true' &&
          ![401, 403, 404].includes(error.status)
        ) {
          if (error.status === 0) {
            this.snackbarService.openSnackBarFailure('Connection failed.');
          } else {
            this.snackbarService.openSnackBarFailure('An error occurred.');
          }
        }

        return throwError(error);
      }),
    );
  }
}
